import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, MessageCircle, RefreshCw, FileText, CheckCircle2, Save, Loader2, List, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
const SERVICE_TYPES = ['Menú Digital Fase 1', 'Menú Digital Fase 2', 'Menú Digital Completo', 'Landing Page', 'KDS Sistema Cocina', 'Delivery System', 'Personalizado'];
const PAYMENT_METHODS = ['SINPE Móvil', 'Transferencia Bancaria', 'Efectivo', 'Otro'];
const PAYMENT_TYPES = ['Pago Total', 'Primer Abono (50%)', 'Segundo Abono (50%)', 'Abono Parcial'];
const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  'Menú Digital Fase 1': 'Implementación de Menú Digital Fase 1. Incluye configuración inicial, carga de menú, y código QR.',
  'Menú Digital Fase 2': 'Implementación de Menú Digital Fase 2. Incluye sistema de pedidos, integración con KDS y reportes básicos.',
  'Menú Digital Completo': 'Sistema integral de Menú Digital. Incluye pedidos, KDS, reportes avanzados y módulo de lealtad.',
  'Landing Page': 'Diseño y desarrollo de Landing Page personalizada con integración al menú digital.',
  'KDS Sistema Cocina': 'Implementación de Kitchen Display System (KDS) para gestión de comandas en cocina.',
  'Delivery System': 'Módulo completo de delivery con asignación de choferes y tracking de órdenes.',
  Personalizado: 'Servicios de desarrollo y configuración personalizados.'
};
export function ReceiptGeneratorPage() {
  const navigate = useNavigate();
  const getNextReceiptNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `NOIZA-${year}-`;
    try {
      const {
        data
      } = await supabase.from('noiza_receipts').select('receipt_number').like('receipt_number', `${prefix}%`).order('receipt_number', {
        ascending: false
      }).limit(1);
      if (data && data.length > 0) {
        const lastNum = parseInt(data[0].receipt_number.replace(prefix, ''), 10);
        return `${prefix}${String((lastNum || 0) + 1).padStart(4, '0')}`;
      }
      return `${prefix}0001`;
    } catch {
      return `${prefix}0001`;
    }
  };
  const [formData, setFormData] = useState({
    receiptNumber: '',
    date: new Date().toISOString().split('T')[0],
    clientName: '',
    clientId: '',
    clientPhone: '',
    serviceType: 'Menú Digital Fase 1',
    serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
    totalAmount: 100000,
    paymentAmount: 50000,
    paymentMethod: 'SINPE Móvil',
    paymentReference: '',
    paymentType: 'Primer Abono (50%)',
    notes: ''
  });
  const [savedReceipts, setSavedReceipts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const remainingBalance = formData.totalAmount - formData.paymentAmount;
  useEffect(() => {
    fetchReceipts();
    // Set initial sequential receipt number
    getNextReceiptNumber().then(num => {
      setFormData(prev => ({
        ...prev,
        receiptNumber: num
      }));
    });
  }, []);
  const fetchReceipts = async () => {
    setLoadingReceipts(true);
    try {
      const {
        data,
        error
      } = await supabase.from('noiza_receipts').select('*').order('created_at', {
        ascending: false
      }).limit(20);
      if (error) throw error;
      if (data) setSavedReceipts(data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    } finally {
      setLoadingReceipts(false);
    }
  };
  const handleSave = async () => {
    if (!formData.clientName) {
      alert('Por favor ingresa el nombre del cliente');
      return;
    }
    setSaving(true);
    try {
      // Check if receipt number already exists
      const {
        data: existing
      } = await supabase.from('noiza_receipts').select('id').eq('receipt_number', formData.receiptNumber).single();
      const payload = {
        receipt_number: formData.receiptNumber,
        receipt_date: formData.date,
        client_name: formData.clientName,
        client_id: formData.clientId,
        client_phone: formData.clientPhone,
        service_type: formData.serviceType,
        service_description: formData.serviceDescription,
        total_amount: formData.totalAmount,
        payment_amount: formData.paymentAmount,
        remaining_balance: remainingBalance,
        payment_method: formData.paymentMethod,
        payment_reference: formData.paymentReference,
        payment_type: formData.paymentType,
        notes: formData.notes
      };
      let error;
      if (existing) {
        // Update
        const {
          error: updateError
        } = await supabase.from('noiza_receipts').update(payload).eq('id', existing.id);
        error = updateError;
      } else {
        // Insert
        const {
          error: insertError
        } = await supabase.from('noiza_receipts').insert([payload]);
        error = insertError;
      }
      if (error) throw error;
      setSaveSuccess(true);
      fetchReceipts();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving receipt:', err);
      alert('Error al guardar el recibo. Revisa la consola para más detalles.');
    } finally {
      setSaving(false);
    }
  };
  const loadReceipt = (receipt: any) => {
    setFormData({
      receiptNumber: receipt.receipt_number,
      date: receipt.receipt_date,
      clientName: receipt.client_name,
      clientId: receipt.client_id || '',
      clientPhone: receipt.client_phone || '',
      serviceType: receipt.service_type,
      serviceDescription: receipt.service_description || '',
      totalAmount: receipt.total_amount,
      paymentAmount: receipt.payment_amount,
      paymentMethod: receipt.payment_method,
      paymentReference: receipt.payment_reference || '',
      paymentType: receipt.payment_type,
      notes: receipt.notes || ''
    });
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      serviceType: newType,
      serviceDescription: DEFAULT_DESCRIPTIONS[newType] || ''
    }));
  };
  const handleReset = async () => {
    const nextNum = await getNextReceiptNumber();
    setFormData({
      receiptNumber: nextNum,
      date: new Date().toISOString().split('T')[0],
      clientName: '',
      clientId: '',
      clientPhone: '',
      serviceType: 'Menú Digital Fase 1',
      serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
      totalAmount: 100000,
      paymentAmount: 50000,
      paymentMethod: 'SINPE Móvil',
      paymentReference: '',
      paymentType: 'Primer Abono (50%)',
      notes: ''
    });
  };
  const handlePrint = () => {
    window.print();
  };
  const handleWhatsAppShare = () => {
    const text = `*RECIBO DE PAGO - NOIZA* 🧾

*Recibo N°:* ${formData.receiptNumber}
*Fecha:* ${formData.date}
*Cliente:* ${formData.clientName}

*Servicio:* ${formData.serviceType}
*Tipo de Pago:* ${formData.paymentType}
*Método:* ${formData.paymentMethod} ${formData.paymentReference ? `(Ref: ${formData.paymentReference})` : ''}

*Monto Pagado:* ${formatCurrency(formData.paymentAmount)}
*Saldo Pendiente:* ${formatCurrency(remainingBalance)}

Gracias por confiar en NOIZA.
Plataforma de Delivery Digital - Costa Rica
noizacr.com`;
    const encodedText = encodeURIComponent(text);
    let phone = formData.clientPhone.replace(/\D/g, '');
    if (phone && !phone.startsWith('506')) {
      phone = '506' + phone;
    }
    const url = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(url, '_blank');
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white print:bg-white print:text-black" data-id="element-4469">
      {/* Print Styles */}
      <style data-id="element-4470">
        {`
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .receipt-container {
              box-shadow: none !important;
              border: none !important;
              padding: 24px !important;
              margin: 0 auto !important;
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 0 !important;
              background: white !important;
              color: black !important;
            }
            .print-text-black { color: black !important; }
            .print-border-black { border-color: #e5e7eb !important; }
            .print-bg-gray { background-color: #f9fafb !important; }
          }
        `}
      </style>

      {/* Header - Hidden in print */}
      <div className="no-print sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4" data-id="element-4471">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-4472">
          <div className="flex items-center gap-4" data-id="element-4473">
            <button onClick={() => navigate('/admin/enrollment')} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-4474">
              <ArrowLeft className="w-5 h-5 text-white/70" data-id="element-4475" />
            </button>
            <div data-id="element-4476">
              <h1 className="text-xl font-bold text-white" data-id="element-4477">
                Generador de Recibos
              </h1>
              <p className="text-sm text-white/50" data-id="element-4478">NOIZA Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-4479">
            <button onClick={handleReset} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-4480">
              <RefreshCw className="w-4 h-4" data-id="element-4481" />
              <span className="hidden sm:inline" data-id="element-4482">Nuevo</span>
            </button>

            <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${saveSuccess ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`} data-id="element-4483">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-4484" /> : saveSuccess ? <CheckCircle2 className="w-4 h-4" data-id="element-4485" /> : <Save className="w-4 h-4" data-id="element-4486" />}
              <span className="hidden sm:inline" data-id="element-4487">
                {saving ? 'Guardando...' : saveSuccess ? 'Guardado' : 'Guardar'}
              </span>
            </button>

            <button onClick={handleWhatsAppShare} className="px-4 py-2 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-4488">
              <MessageCircle className="w-4 h-4" data-id="element-4489" />
              <span className="hidden sm:inline" data-id="element-4490">WhatsApp</span>
            </button>
            <button onClick={handlePrint} className="px-4 py-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#C9A34E]/90 rounded-lg text-sm font-bold transition-colors flex items-center gap-2" data-id="element-4491">
              <Printer className="w-4 h-4" data-id="element-4492" />
              <span className="hidden sm:inline" data-id="element-4493">Imprimir / PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-4494">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-4495">
          {/* Form Section */}
          <div className="lg:col-span-5 space-y-6 no-print" data-id="element-4496">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6" data-id="element-4497">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-4498">
                <FileText className="w-5 h-5 text-[#C9A34E]" data-id="element-4499" />
                Datos del Recibo
              </h2>

              <div className="space-y-5" data-id="element-4500">
                <div className="grid grid-cols-2 gap-4" data-id="element-4501">
                  <div data-id="element-4502">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4503">
                      N° de Recibo
                    </label>
                    <input type="text" value={formData.receiptNumber} onChange={e => setFormData({
                    ...formData,
                    receiptNumber: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4504" />
                  </div>
                  <div data-id="element-4505">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4506">
                      Fecha
                    </label>
                    <input type="date" value={formData.date} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4507" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-4508">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-4509">
                    Cliente
                  </h3>
                  <div data-id="element-4510">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4511">
                      Nombre / Razón Social
                    </label>
                    <input type="text" value={formData.clientName} onChange={e => setFormData({
                    ...formData,
                    clientName: e.target.value
                  })} placeholder="Ej. Restaurante El Sabor" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4512" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-4513">
                    <div data-id="element-4514">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4515">
                        Cédula (Opcional)
                      </label>
                      <input type="text" value={formData.clientId} onChange={e => setFormData({
                      ...formData,
                      clientId: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4516" />
                    </div>
                    <div data-id="element-4517">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4518">
                        Teléfono / WhatsApp
                      </label>
                      <input type="text" value={formData.clientPhone} onChange={e => setFormData({
                      ...formData,
                      clientPhone: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4519" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-4520">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-4521">
                    Servicio
                  </h3>
                  <div data-id="element-4522">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4523">
                      Tipo de Servicio
                    </label>
                    <select value={formData.serviceType} onChange={handleServiceTypeChange} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4524">
                      {SERVICE_TYPES.map(type => <option key={type} value={type} data-id="element-4525">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-4526">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4527">
                      Descripción
                    </label>
                    <textarea value={formData.serviceDescription} onChange={e => setFormData({
                    ...formData,
                    serviceDescription: e.target.value
                  })} rows={3} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-4528" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-4529">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-4530">
                    Pago
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-4531">
                    <div data-id="element-4532">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4533">
                        Monto Total (Contrato)
                      </label>
                      <div className="relative" data-id="element-4534">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-4535">
                          ₡
                        </span>
                        <input type="number" value={formData.totalAmount} onChange={e => setFormData({
                        ...formData,
                        totalAmount: Number(e.target.value)
                      })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4536" />
                      </div>
                    </div>
                    <div data-id="element-4537">
                      <label className="block text-xs font-medium text-[#C9A34E] mb-1.5" data-id="element-4538">
                        Monto de este Pago
                      </label>
                      <div className="relative" data-id="element-4539">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A34E]/60" data-id="element-4540">
                          ₡
                        </span>
                        <input type="number" value={formData.paymentAmount} onChange={e => setFormData({
                        ...formData,
                        paymentAmount: Number(e.target.value)
                      })} className="w-full bg-[#C9A34E]/10 border border-[#C9A34E]/30 rounded-lg pl-8 pr-3 py-2 text-[#C9A34E] font-bold text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4541" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-4542">
                    <div data-id="element-4543">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4544">
                        Tipo de Pago
                      </label>
                      <select value={formData.paymentType} onChange={e => setFormData({
                      ...formData,
                      paymentType: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4545">
                        {PAYMENT_TYPES.map(type => <option key={type} value={type} data-id="element-4546">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-4547">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4548">
                        Método de Pago
                      </label>
                      <select value={formData.paymentMethod} onChange={e => setFormData({
                      ...formData,
                      paymentMethod: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4549">
                        {PAYMENT_METHODS.map(method => <option key={method} value={method} data-id="element-4550">
                            {method}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-4551">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4552">
                      Referencia (Opcional)
                    </label>
                    <input type="text" value={formData.paymentReference} onChange={e => setFormData({
                    ...formData,
                    paymentReference: e.target.value
                  })} placeholder="Ej. Comprobante #123456" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4553" />
                  </div>

                  <div data-id="element-4554">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4555">
                      Notas Adicionales
                    </label>
                    <textarea value={formData.notes} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} rows={2} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-4556" />
                  </div>
                </div>
              </div>
            </div>

            {/* Saved Receipts Panel */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden" data-id="element-4557">
              <button onClick={() => setShowSavedList(!showSavedList)} className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors" data-id="element-4558">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-4559">
                  <List className="w-5 h-5 text-blue-400" data-id="element-4560" />
                  Recibos Guardados
                  <span className="bg-white/10 text-white/70 text-xs py-0.5 px-2 rounded-full ml-2" data-id="element-4561">
                    {savedReceipts.length}
                  </span>
                </h2>
                {showSavedList ? <ChevronUp className="w-5 h-5 text-white/50" data-id="element-4562" /> : <ChevronDown className="w-5 h-5 text-white/50" data-id="element-4563" />}
              </button>

              {showSavedList && <div className="border-t border-white/10 p-4 max-h-96 overflow-y-auto custom-scrollbar" data-id="element-4564">
                  {loadingReceipts ? <div className="flex items-center justify-center py-8" data-id="element-4565">
                      <Loader2 className="w-6 h-6 text-white/30 animate-spin" data-id="element-4566" />
                    </div> : savedReceipts.length === 0 ? <div className="text-center py-8 text-white/40 text-sm" data-id="element-4567">
                      No hay recibos guardados aún.
                    </div> : <div className="space-y-2" data-id="element-4568">
                      {savedReceipts.map(receipt => <button key={receipt.id} onClick={() => loadReceipt(receipt)} className="w-full text-left bg-[#0B0B0B] hover:bg-white/5 border border-white/5 rounded-xl p-4 transition-colors group flex items-center justify-between" data-id="element-4569">
                          <div data-id="element-4570">
                            <div className="flex items-center gap-2 mb-1" data-id="element-4571">
                              <span className="text-sm font-bold text-white" data-id="element-4572">
                                {receipt.receipt_number}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-white/10 text-white/70" data-id="element-4573">
                                {receipt.payment_type}
                              </span>
                            </div>
                            <p className="text-sm text-white/70 font-medium" data-id="element-4574">
                              {receipt.client_name}
                            </p>
                            <p className="text-xs text-white/40 mt-1" data-id="element-4575">
                              {new Date(receipt.receipt_date).toLocaleDateString('es-CR')}{' '}
                              • {receipt.service_type}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-4576">
                            <p className="text-sm font-bold text-[#C9A34E]" data-id="element-4577">
                              {formatCurrency(receipt.payment_amount)}
                            </p>
                            <p className="text-xs text-white/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4578">
                              Ver recibo →
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-7 print:col-span-12" data-id="element-4579">
            <div className="sticky top-24 print:static" data-id="element-4580">
              <h2 className="text-sm font-semibold text-white/60 mb-4 uppercase tracking-wider no-print" data-id="element-4581">
                Vista Previa del Recibo
              </h2>

              {/* THE RECEIPT */}
              <div className="receipt-container bg-white text-black rounded-xl p-8 sm:p-12 shadow-2xl shadow-black/50 max-w-2xl mx-auto border border-white/20 print:shadow-none print:border-none print:p-0 print:max-w-full print:rounded-none" data-id="element-4582">
                {/* Header */}
                <div className="flex justify-between items-start mb-10" data-id="element-4583">
                  <div data-id="element-4584">
                    <div className="flex items-center gap-2 mb-2" data-id="element-4585">
                      <div className="w-10 h-10 bg-[#0B0B0B] rounded-lg flex items-center justify-center print-bg-gray" data-id="element-4586">
                        <span className="text-[#C9A34E] font-bold text-2xl leading-none" data-id="element-4587">
                          N
                        </span>
                      </div>
                      <span className="font-bold text-3xl tracking-tight text-[#0B0B0B] print-text-black" style={{
                      fontFamily: "'Playfair Display', serif"
                    }} data-id="element-4588">
                        NOIZA
                      </span>
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-4589">
                      Plataforma de Delivery Digital
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-4590">Costa Rica</p>
                    <p className="text-sm text-gray-500" data-id="element-4591">noizacr.com</p>
                  </div>
                  <div className="text-right" data-id="element-4592">
                    <h2 className="text-3xl font-black text-gray-200 mb-2 uppercase tracking-widest" data-id="element-4593">
                      Recibo
                    </h2>
                    <p className="text-sm font-bold text-gray-800" data-id="element-4594">
                      N° {formData.receiptNumber}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-4595">
                      Fecha:{' '}
                      {new Date(formData.date).toLocaleDateString('es-CR')}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-100 print-bg-gray print-border-black" data-id="element-4596">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-4597">
                    Recibí de:
                  </h3>
                  <p className="text-lg font-bold text-gray-800" data-id="element-4598">
                    {formData.clientName || 'Nombre del Cliente'}
                  </p>
                  <div className="flex gap-6 mt-1 text-sm text-gray-600" data-id="element-4599">
                    {formData.clientId && <p data-id="element-4600">
                        <span className="font-semibold" data-id="element-4601">Cédula:</span>{' '}
                        {formData.clientId}
                      </p>}
                    {formData.clientPhone && <p data-id="element-4602">
                        <span className="font-semibold" data-id="element-4603">Tel/WA:</span>{' '}
                        {formData.clientPhone}
                      </p>}
                  </div>
                </div>

                {/* Service Details */}
                <div className="mb-8" data-id="element-4604">
                  <table className="w-full text-left border-collapse" data-id="element-4605">
                    <thead data-id="element-4606">
                      <tr className="border-b-2 border-gray-200" data-id="element-4607">
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-4608">
                          Descripción del Servicio
                        </th>
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right" data-id="element-4609">
                          Total Contrato
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-4610">
                      <tr className="border-b border-gray-100" data-id="element-4611">
                        <td className="py-4 pr-4" data-id="element-4612">
                          <p className="font-bold text-gray-800 mb-1" data-id="element-4613">
                            {formData.serviceType}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-4614">
                            {formData.serviceDescription}
                          </p>
                        </td>
                        <td className="py-4 text-right font-semibold text-gray-800 align-top" data-id="element-4615">
                          {formatCurrency(formData.totalAmount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Payment Breakdown */}
                <div className="flex justify-end mb-10" data-id="element-4616">
                  <div className="w-full sm:w-1/2 space-y-3" data-id="element-4617">
                    <div className="flex justify-between text-sm text-gray-600" data-id="element-4618">
                      <span data-id="element-4619">Total del Contrato:</span>
                      <span data-id="element-4620">{formatCurrency(formData.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-[#0B0B0B] border-y border-gray-200 py-3 print-text-black" data-id="element-4621">
                      <span data-id="element-4622">Monto Pagado:</span>
                      <span data-id="element-4623">{formatCurrency(formData.paymentAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium text-gray-500" data-id="element-4624">
                      <span data-id="element-4625">Saldo Pendiente:</span>
                      <span data-id="element-4626">{formatCurrency(remainingBalance)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Info & Notes */}
                <div className="grid grid-cols-2 gap-8 mb-12" data-id="element-4627">
                  <div data-id="element-4628">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-4629">
                      Detalles del Pago
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-4630">
                      <span className="font-semibold" data-id="element-4631">Tipo:</span>{' '}
                      {formData.paymentType}
                    </p>
                    <p className="text-sm text-gray-800" data-id="element-4632">
                      <span className="font-semibold" data-id="element-4633">Método:</span>{' '}
                      {formData.paymentMethod}
                    </p>
                    {formData.paymentReference && <p className="text-sm text-gray-800" data-id="element-4634">
                        <span className="font-semibold" data-id="element-4635">Referencia:</span>{' '}
                        {formData.paymentReference}
                      </p>}
                  </div>
                  {formData.notes && <div data-id="element-4636">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-4637">
                        Notas
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap" data-id="element-4638">
                        {formData.notes}
                      </p>
                    </div>}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 pt-6 flex justify-between items-end relative" data-id="element-4639">
                  <div data-id="element-4640">
                    <p className="text-xs text-gray-400 font-medium" data-id="element-4641">
                      Este recibo es un comprobante de pago válido.
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-1" data-id="element-4642">
                      Generado por NOIZA - Plataforma de Delivery Digital
                    </p>
                  </div>

                  {/* Watermark/Stamp */}
                  <div className="absolute right-0 bottom-4 opacity-10 rotate-[-15deg] pointer-events-none" data-id="element-4643">
                    <div className="border-4 border-black rounded-lg px-6 py-2" data-id="element-4644">
                      <span className="text-4xl font-black tracking-widest uppercase" data-id="element-4645">
                        {formData.paymentAmount >= formData.totalAmount ? 'Pagado' : 'Abono'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}